import {
  AiReviewStatus,
  DispatchStatus,
  JobState,
  OrderState,
  SiteType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { startCase, uniqBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { dispatchStatusLabelKeys, OrdersDispatchStorageKeys } from '~constants/dispatch';
import { SelectedFilter } from '~constants/enums';
import { useStores } from '~store';
import { Filters as OrdersDispatchStoreFilters } from '~store/OrdersDispatchStore';

// oxlint-disable-next-line @typescript-eslint/no-unused-vars
const ordersFilters: Array<
  Extract<
    keyof OrdersDispatchStoreFilters,
    | 'aiReviewStatuses'
    | 'collaboratorIds'
    | 'customerAccountIds'
    | 'departmentIds'
    | 'dispatchNumberIds'
    | 'dispatchStatuses'
    | 'driverIds'
    | 'dropoffSites'
    | 'equipmentIds'
    | 'equipmentOwnerCompanyIds'
    | 'equipmentTypes'
    | 'foremen'
    | 'jobStates'
    | 'materialIds'
    | 'pickUpSites'
    | 'prevailingWage'
    | 'projectIds'
    | 'projectsExternalIds'
    | 'refinedOrderStates'
    | 'requesterIds'
    | 'salespersonIds'
    | 'serviceClassIds'
    | 'siteTypes'
    | 'ticketPotentialDuplicate'
    | 'ticketTypes'
    | 'vendorAccountIds'
  >
> = [
  'aiReviewStatuses',
  'collaboratorIds',
  'customerAccountIds',
  'departmentIds',
  'dispatchNumberIds',
  'dispatchStatuses',
  'driverIds',
  'dropoffSites',
  'equipmentIds',
  'equipmentOwnerCompanyIds',
  'equipmentTypes',
  'foremen',
  'jobStates',
  'materialIds',
  'pickUpSites',
  'prevailingWage',
  'projectIds',
  'projectsExternalIds',
  'refinedOrderStates',
  'requesterIds',
  'salespersonIds',
  'serviceClassIds',
  'siteTypes',
  'ticketPotentialDuplicate',
  'ticketTypes',
  'vendorAccountIds',
];
type OrdersFilters = (typeof ordersFilters)[number];

type OrdersDispatchFilterKey = OrdersFilters;

type AppliedFiltersState = Record<
  SelectedFilter,
  {
    ordersDispatchFilterKey: OrdersDispatchFilterKey;
    search: string;
  }
>;

export const ordersDispatchFilterKeyBySelectedFilter: Record<
  SelectedFilter,
  OrdersDispatchFilterKey
> = {
  [SelectedFilter.AI_REVIEW_STATUSES]: 'aiReviewStatuses',
  [SelectedFilter.COLLABORATORS]: 'collaboratorIds',
  [SelectedFilter.CUSTOMERS]: 'customerAccountIds',
  [SelectedFilter.DEPARTMENTS]: 'departmentIds',
  [SelectedFilter.DISPATCH_NUMBERS]: 'dispatchNumberIds',
  [SelectedFilter.DISPATCH_STATUSES]: 'dispatchStatuses',
  [SelectedFilter.DRIVERS]: 'driverIds',
  [SelectedFilter.DROP_OFF_SITES]: 'dropoffSites',
  [SelectedFilter.DUPLICATE_TICKETS]: 'ticketPotentialDuplicate',
  [SelectedFilter.EQUIPMENT]: 'equipmentIds',
  [SelectedFilter.EQUIPMENT_OWNERS]: 'equipmentOwnerCompanyIds',
  [SelectedFilter.EQUIPMENT_TYPES]: 'equipmentTypes',
  [SelectedFilter.FOREMEN]: 'foremen',
  [SelectedFilter.JOB_STATES]: 'jobStates',
  [SelectedFilter.MATERIALS]: 'materialIds',
  [SelectedFilter.ORDER_STATES_REFINED]: 'refinedOrderStates',
  [SelectedFilter.PICK_UP_SITES]: 'pickUpSites',
  [SelectedFilter.PREVAILING_WAGE]: 'prevailingWage',
  [SelectedFilter.PROJECTS]: 'projectIds',
  [SelectedFilter.PROJECTS_EXTERNAL_IDS]: 'projectsExternalIds',
  [SelectedFilter.REQUESTERS]: 'requesterIds',
  [SelectedFilter.SALESPEOPLE]: 'salespersonIds',
  [SelectedFilter.SERVICE_CLASSES]: 'serviceClassIds',
  [SelectedFilter.SITE_TYPES]: 'siteTypes',
  [SelectedFilter.TICKET_TYPES]: 'ticketTypes',
  [SelectedFilter.VENDORS]: 'vendorAccountIds',
};

type FiltersAvailableOptionsState = Record<SelectedFilter, FilterMenuOption[]>;

const getAppliedFiltersIninitialState = () => {
  const initialState = Object.entries(ordersDispatchFilterKeyBySelectedFilter).reduce(
    (obj, [filterKey, storeKey]) => {
      const key = filterKey as SelectedFilter;

      obj[key] = {
        ordersDispatchFilterKey: storeKey,
        search: '',
      };

      return obj;
    },
    {} as AppliedFiltersState,
  );

  return initialState;
};

const AVAILABLE_OPTIONS_STORAGE_KEY = 'ordersDispatch_availableOptions';
type AppliedFiltersStoredAvailableOptions = Partial<
  Record<SelectedFilter, FilterMenuOption[]>
>;

// Given that hooks are not singletons, different components consuming this hook will have
// different copies of the "availableOptions" array, hence an update in one component will not be
// reflected in the other components. This means that later, when the "availableOptions" array is
// persisted in the local storage, options fetched by a component will override the options fetched
// by the other components. To avoid this, we flag these filters as "shared", so to ensure that we
// need to grab from the store the already saved options for the filters in question before
// re-persisting them in the local storage.
const ORDERS_DISPATCH_SHARED_FILTERS_KEYS: SelectedFilter[] = [
  SelectedFilter.COLLABORATORS,
  SelectedFilter.DEPARTMENTS,
  SelectedFilter.EQUIPMENT,
];

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (
    stored ? JSON.parse(stored) : {}
  ) as AppliedFiltersStoredAvailableOptions;

  const initialState = {
    ...Object.values(SelectedFilter).reduce((obj, filter) => {
      obj[filter] = parsed[filter] ?? [];

      return obj;
    }, {} as FiltersAvailableOptionsState),
  };

  initialState.aiReviewStatuses = Object.values(AiReviewStatus).map((status) => ({
    label: startCase(status),
    value: `${status}`,
  }));

  initialState.dispatchStatuses = Object.values(DispatchStatus).map((status) => ({
    label: t(dispatchStatusLabelKeys[status]),
    value: `${status}`,
  }));

  initialState.jobStates = Object.values(JobState).map((state) => ({
    label: t(`status.${state}`),
    value: `${state}`,
  }));

  initialState.refinedOrderStates = Object.values(OrderState)
    .filter((state) => state !== OrderState.PENDING_REQUEST)
    .map((state) => ({ label: t(`status.${state}`), value: `${state}` }));

  initialState.siteTypes = Object.values(SiteType)
    .filter((type) => type !== SiteType.EQUIPMENT_HOME_BASE)
    .map((type) => ({ label: t(`site_types.${type}`), value: `${type}` }));

  initialState.duplicateTickets = [
    {
      label: t('dispatch.dispatch_v2.filters.potential_duplicates'),
      value: 'true',
    },
  ];

  initialState.prevailingWage = [
    {
      label: t('dispatch.dispatch_v2.filters.prevailing_wage_yes'),
      value: 'true',
    },
    {
      label: t('dispatch.dispatch_v2.filters.prevailing_wage_no'),
      value: 'false',
    },
  ];

  initialState.ticketTypes = [
    {
      label: t('dispatch.dispatch_v2.filters.digital_ticket_orders'),
      value: 'true',
    },
    {
      label: t('dispatch.dispatch_v2.filters.paper_ticket_orders'),
      value: 'false',
    },
  ];

  return initialState;
};

const getAppliedFiltersChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, options]) => {
    obj[key as SelectedFilter] = options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

export default function useOrdersDispatchFilters() {
  const { ordersDispatchStore } = useStores();

  const [appliedFilters, setAppliedFilters] = useState(getAppliedFiltersIninitialState());
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );

  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFiltersChipsCollectedDataInitialState(availableOptions),
  );

  const updateAppliedFilterChipsCollectedData = useCallback(
    (filter: string, data: FilterMenuOption[]) => {
      const isValidFilter =
        ordersDispatchFilterKeyBySelectedFilter[filter as SelectedFilter];

      if (isValidFilter) {
        setAppliedFilterChipsCollectedData((state) => ({
          ...state,
          [filter]: uniqBy([...(state[filter] ?? []), ...data], (i) => i.value),
        }));
      }
    },
    [],
  );

  const defaultDispatchOrdersFilters = useMemo(() => {
    const fromStore = ordersDispatchStore.retrieveStoredFilters();

    return {
      endDate: ordersDispatchStore.filters.endDate ?? fromStore.endDate,
      orderStates: ordersDispatchStore.filters.orderStates ?? fromStore.orderStates,
      search: '',
      startDate: ordersDispatchStore.filters.startDate ?? fromStore.startDate,
    };
  }, [ordersDispatchStore.filters]);

  // Only persist the date and order states when company changes
  const handleDispatchOrdersFiltersReset = useCallback(() => {
    localStorage.removeItem(OrdersDispatchStorageKeys.APPLIED_FILTERS);
    ordersDispatchStore.setFilters(defaultDispatchOrdersFilters);
  }, [ordersDispatchStore, defaultDispatchOrdersFilters]);

  useEffect(() => {
    ordersDispatchStore.restoreFilters();
  }, []);

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, options]) => {
      const filterKey = key as SelectedFilter;
      const isSharedFilter = ORDERS_DISPATCH_SHARED_FILTERS_KEYS.includes(filterKey);

      if (isSharedFilter) {
        const alreadyStoredOptions = getAvailableOptionsInitialState()[filterKey];

        obj[filterKey] = uniqBy(
          [...(alreadyStoredOptions ?? []), ...options],
          (i) => i.value,
        );
      } else {
        obj[filterKey] = options;
      }

      return obj;
    }, {} as AppliedFiltersStoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
    ordersDispatchStore.commitPendingFiltersUpdateAfterAssigneeUpdateInLocalStorageAvailebleOptions();
  }, [JSON.stringify(availableOptions)]);

  useEffect(() => {
    if (
      ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdate &&
      !ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions
    ) {
      setAvailableOptions((options) => ({
        ...options,
        drivers: availableOptions.drivers.map((option) => {
          if (
            option.value ===
            ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdate?.value
          ) {
            return { ...ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdate };
          }

          return option;
        }),
        vendors: availableOptions.vendors.map((option) => {
          if (
            option.value ===
            ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdate?.value
          ) {
            return { ...ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdate };
          }

          return option;
        }),
      }));
    }
  }, [
    ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdate,
    ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions,
  ]);

  const updateAvailableOptions = useCallback(
    (key: SelectedFilter, data: FilterMenuOption[]) => {
      setAvailableOptions((state) => {
        const values = uniqBy([...(state[key] ?? []), ...data], (i) => i.value);

        return { ...state, [key]: values };
      });
    },
    [],
  );

  return {
    appliedFilters,
    updateAppliedFilters: setAppliedFilters,
    availableOptions,
    updateAvailableOptions,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
    defaultDispatchOrdersFilters,
    handleDispatchOrdersFiltersReset,
  };
}
